/**
 * Make the cited references contiguous.
 *
 * The backend assigns hierarchical citation ids to ALL retrieved chunks
 * (1.1, 1.2, ... 1.8), but the answer cites only a subset (e.g. the model skips
 * 1.7). After filtering to the cited sources this leaves visible gaps in the
 * source table ("1.1 .. 1.6, 1.8" — 1.7 missing), which is confusing.
 *
 * This remaps the cited ids to a contiguous sequence per document — keeping the
 * document prefix and the existing (document-ordered) order — and rewrites BOTH
 * the answer text markers (`[[old]]` -> `[[new]]`) and each source's
 * `citation_id`, so chips and rows stay perfectly in sync.
 *
 * Whole-document entries (bare "N", no dot) keep their integer; only the
 * per-section sub-indices are renumbered 1, 2, 3 ... within each document.
 */

import { ChatSource } from '../../api/chatTypes';

/** Compare two citation ids ("1", "1.2", "1.10") in natural numeric order. */
function compareIds(a: string, b: string): number {
  const pa = a.split('.').map((p) => parseInt(p, 10));
  const pb = b.split('.').map((p) => parseInt(p, 10));
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = pa[i] ?? -1;
    const nb = pb[i] ?? -1;
    if (na !== nb) {
      return na - nb;
    }
  }
  return 0;
}

export interface RenumberResult {
  content: string;
  sources: ChatSource[];
}

/**
 * Build a remap old-id -> new-id that makes each document's sub-indices
 * contiguous, then apply it to the answer text and the source list.
 *
 * Exported result is a new content string and a new sources array; inputs are
 * not mutated.
 */
export function renumberCitations(content: string, sources: ChatSource[]): RenumberResult {
  if (sources.length === 0) {
    return { content, sources };
  }

  // Collect the distinct cited ids (as strings) present on the sources.
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const s of sources) {
    if (s.citation_id === null || s.citation_id === undefined) {
      continue;
    }
    const id = String(s.citation_id);
    if (!seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  if (ids.length === 0) {
    return { content, sources };
  }

  // Group by document prefix, preserving first-appearance order of documents,
  // and renumber the documents themselves contiguously too (1, 2, 3 ...).
  const docOrder: string[] = [];
  const byDoc = new Map<string, string[]>();
  for (const id of ids) {
    const doc = id.split('.')[0];
    if (!byDoc.has(doc)) {
      byDoc.set(doc, []);
      docOrder.push(doc);
    }
    byDoc.get(doc)!.push(id);
  }

  // remap: old id (string) -> new id (string)
  const remap = new Map<string, string>();
  docOrder.forEach((oldDoc, docIdx) => {
    const newDoc = String(docIdx + 1);
    const groupIds = byDoc.get(oldDoc)!.slice().sort(compareIds);
    let sub = 0;
    for (const oldId of groupIds) {
      if (!oldId.includes('.')) {
        // Whole-document entry: keep it bare, just renumber the document.
        remap.set(oldId, newDoc);
      } else {
        sub += 1;
        remap.set(oldId, `${newDoc}.${sub}`);
      }
    }
  });

  // If nothing actually changes, return the originals (avoid needless rebuilds).
  let changed = false;
  for (const [oldId, newId] of remap) {
    if (oldId !== newId) {
      changed = true;
      break;
    }
  }
  if (!changed) {
    return { content, sources };
  }

  // Rewrite the answer-text markers. Match [[id]] where id is \d+(\.\d+)? and
  // replace with the remapped id (leave unknown ids untouched — they will be
  // stripped later by the orphan filter).
  const newContent = content.replace(/\[\[(\d+(?:\.\d+)?)\]\]/g, (full, id: string) => {
    const mapped = remap.get(id);
    return mapped ? `[[${mapped}]]` : full;
  });

  // Rewrite the source citation_ids.
  const newSources: ChatSource[] = sources.map((s) => {
    if (s.citation_id === null || s.citation_id === undefined) {
      return s;
    }
    const mapped = remap.get(String(s.citation_id));
    return mapped ? { ...s, citation_id: mapped } : s;
  });

  return { content: newContent, sources: newSources };
}
